// ---------- Geometria da etiqueta: layout, tipografia e rodapé ----------
//
// Fonte única das regras que decidem COMO a etiqueta é montada — o limiar entre
// os dois layouts, a faixa de dimensões aceita no editor, os pesos de fonte do
// auto-fit e as proporções do rodapé / da coluna do QR.
//
// Antes destas constantes viverem aqui, cada regra estava escrita duas vezes: em
// `packages/ui-shared/src/components/LabelPreview.tsx` (preview web, SVG em mm) e
// em `apps/android/.../core/printing/image/LabelImageRenderer.kt` (bitmap que vai
// para a impressora, em dots). O único elo era um comentário — "Match exato com
// web `fontMmFor`" — e foi assim que o limiar de layout virou 30mm no Android e
// continuou inexistente na web, com o preview mostrando uma etiqueta que a
// impressora não produzia.
//
// Espelho Kotlin manual (mesma duplicação intencional dos enums/schemas — ver
// CLAUDE.md): `apps/android/.../core/printing/LabelSpec.kt` (limiar),
// `.../image/LabelImageRenderer.kt` (tipografia e proporções) e
// `apps/android/.../domain/model/LabelTemplate.kt` (faixa do editor). O Kotlin
// trabalha em **dots @203dpi** (8 dots/mm); aqui tudo é **mm**, com a
// equivalência anotada em cada valor. Divergiu? O Kotlin é quem imprime: ele é a
// referência e este arquivo acompanha.

// ---------- Layout ----------

/**
 * Altura (mm) até a qual a etiqueta sai em **2 colunas** — conteúdo à esquerda,
 * QR + PIN + logo empilhados à direita. Acima disso é **1 coluna** com rodapé
 * horizontal, e nada mais: a largura não entra na decisão (owner, 2026-09-05).
 *
 * Era 35mm com comparação exclusiva; etiqueta de 31 a 34mm de altura passou a
 * sair em 1 coluna. Kotlin: `LabelSpec.SMALL_LABEL_THRESHOLD_MM`.
 */
export const SMALL_LABEL_THRESHOLD_MM = 30;

/** `true` quando a etiqueta usa o layout de 2 colunas. */
export function usesTwoColumnLayout(heightMm: number): boolean {
  return heightMm <= SMALL_LABEL_THRESHOLD_MM;
}

// ---------- Faixa aceita no editor de modelos ----------
//
// Vale para o que o usuário DIGITA. Modelo já salvo fora da faixa continua sendo
// lido e impresso — por isso `LabelTemplateSchema` (leitura) segue permissivo e
// só `Create/UpdateLabelTemplateInput` (escrita) aplicam estes limites.

/** Nada mais estreito que o rolo mais fino vendido. */
export const MIN_TEMPLATE_WIDTH_MM = 40;
/** Teto que acomoda o 102×152mm (4×6"), o maior tamanho em uso. */
export const MAX_TEMPLATE_WIDTH_MM = 110;
export const MIN_TEMPLATE_HEIGHT_MM = 25;
export const MAX_TEMPLATE_HEIGHT_MM = 160;

export function isTemplateWidthValid(widthMm: number): boolean {
  return (
    Number.isInteger(widthMm) &&
    widthMm >= MIN_TEMPLATE_WIDTH_MM &&
    widthMm <= MAX_TEMPLATE_WIDTH_MM
  );
}

export function isTemplateHeightValid(heightMm: number): boolean {
  return (
    Number.isInteger(heightMm) &&
    heightMm >= MIN_TEMPLATE_HEIGHT_MM &&
    heightMm <= MAX_TEMPLATE_HEIGHT_MM
  );
}

// ---------- Tipografia do auto-fit ----------

/**
 * Tamanho de fonte base (mm) ANTES de aplicar peso e fillFactor. Com
 * `fillFactor = 1.0`, NORMAL (peso 1.0) cai em 4mm. Kotlin: `BASE_FONT_MM`.
 */
export const BASE_FONT_MM = 4.0;
/** Piso de legibilidade (~18 dots @203dpi). Nenhuma fonte fica menor. */
export const MIN_FONT_MM = 2.25;
/** Teto — preenche etiqueta grande com poucos campos sem exagerar. */
export const MAX_FONT_MM = 18.0;

/**
 * `TextScale` → PESO relativo sobre [BASE_FONT_MM]. Mantém a hierarquia:
 * nome do produto e validade (LARGE) maiores, captions (SMALL/XSMALL) menores.
 * `XXSMALL` só é usado pelo branding do Android, mas o peso vive aqui para os
 * dois lados responderem igual. Kotlin: `textScaleWeight`.
 */
export function textScaleWeight(scale: string): number {
  switch (scale) {
    case 'XXSMALL':
      return 0.6;
    case 'XSMALL':
      return 0.75;
    case 'SMALL':
      return 0.85;
    case 'NORMAL':
      return 1.0;
    case 'LARGE':
      return 1.4;
    case 'XLARGE':
      return 1.8;
    default:
      return 1.0;
  }
}

/** Fonte final (mm) de uma escala, escalada pelo fillFactor e clampada. */
export function fontMmFor(scale: string, fillFactor: number): number {
  const raw = textScaleWeight(scale) * BASE_FONT_MM * fillFactor;
  return Math.max(MIN_FONT_MM, Math.min(MAX_FONT_MM, raw));
}

// ---------- Rodapé (layout de 1 coluna) ----------

/** Fatia da altura reservada ao rodapé. Kotlin: `FOOTER_HEIGHT_RATIO`. */
export const FOOTER_HEIGHT_RATIO = 0.25;
/** Kotlin: `FOOTER_HEIGHT_MIN = 80` dots. */
export const FOOTER_HEIGHT_MIN_MM = 10;
/** Kotlin: `FOOTER_HEIGHT_MAX = 200` dots. */
export const FOOTER_HEIGHT_MAX_MM = 25;

/**
 * Altura reservada ao rodapé `[QR] [PIN] [logo]`, em mm.
 *
 * O QR é quadrado e divide a faixa com PIN e logo, então a metade da largura
 * útil é o teto natural. **O piso cede a esse teto** — antes o
 * `Math.max(FOOTER_HEIGHT_MIN_MM, …)` ficava por fora e devolvia 10mm mesmo numa
 * etiqueta que não os comportava; no Android o mesmo descuido fazia o `coerceIn`
 * receber máximo abaixo do mínimo e **lançar** (issue #360). Sem QR não há
 * rodapé nenhum.
 */
export function footerHeightMm(
  contentWidthMm: number,
  contentHeightMm: number,
  hasQr = true,
): number {
  if (!hasQr) return 0;
  const widthCap = contentWidthMm / 2;
  const maxSide = Math.min(FOOTER_HEIGHT_MAX_MM, widthCap);
  if (maxSide <= 0) return 0;
  const target = contentHeightMm * FOOTER_HEIGHT_RATIO;
  const floor = Math.min(FOOTER_HEIGHT_MIN_MM, maxSide);
  return Math.max(floor, Math.min(target, maxSide));
}

/**
 * Fatia da largura útil que o PIN de 6 dígitos ocupa no rodapé, e o teto da sua
 * altura em fração do lado do QR: o PIN cresce com a etiqueta e encolhe na
 * estreita, sem passar por cima do QR e da logo. Kotlin:
 * `FOOTER_CODE_WIDTH_RATIO` / `FOOTER_CODE_MAX_HEIGHT_RATIO`.
 */
export const FOOTER_CODE_WIDTH_RATIO = 0.22;
export const FOOTER_CODE_MAX_HEIGHT_RATIO = 0.35;

// ---------- Coluna direita (layout de 2 colunas) ----------

/** Canaleta entre as colunas. Kotlin: `TWO_COLUMN_GAP_MM`. */
export const TWO_COLUMN_GAP_MM = 1;
/** Alvo da coluna direita. Kotlin: `TWO_COLUMN_QR_TARGET = 100` dots. */
export const TWO_COLUMN_QR_TARGET_MM = 12.5;
/** Abaixo disso o QR V2 deixa de ser lido. Kotlin: `TWO_COLUMN_QR_MIN_SIDE = 75` dots. */
export const TWO_COLUMN_QR_MIN_SIDE_MM = 9.375;
/** Teto da coluna direita em fração da largura. Kotlin: `TWO_COLUMN_RIGHT_MAX_RATIO`. */
export const TWO_COLUMN_RIGHT_MAX_RATIO = 0.38;
/** Fatia da coluna direita ocupada pelo PIN. Kotlin: `COLUMN_CODE_WIDTH_RATIO`. */
export const COLUMN_CODE_WIDTH_RATIO = 0.92;
/** Quanto o PIN pode crescer sobre o tamanho nominal. Kotlin: `CODE_MAX_GROWTH`. */
export const CODE_MAX_GROWTH = 2.5;

/**
 * Largura da coluna direita. Ela **cede**: parte do alvo do QR e encolhe até
 * [TWO_COLUMN_QR_MIN_SIDE_MM] enquanto isso ajudar a coluna esquerda a caber um
 * `rótulo: valor` inteiro numa linha ([leftDemandMm] é essa demanda, medida na
 * fonte mínima). Antes a coluna era dimensionada pelo texto "EtiquetaChef", que
 * media mais que o próprio QR e empurrava a divisória até a metade da etiqueta.
 */
export function twoColumnRightWidthMm(
  contentWidthMm: number,
  leftDemandMm: number,
): number {
  const rightMax = Math.min(
    TWO_COLUMN_QR_TARGET_MM,
    contentWidthMm * TWO_COLUMN_RIGHT_MAX_RATIO,
  );
  const rightMin = Math.min(TWO_COLUMN_QR_MIN_SIDE_MM, rightMax);
  const leftover = contentWidthMm - TWO_COLUMN_GAP_MM - leftDemandMm;
  return Math.max(rightMin, Math.min(rightMax, leftover));
}
